import { Tooltip, Text } from "@mantine/core";
import { DataTable, Badge, StatusBadge } from "@xplatform/design-system";
import type { DataTableColumn } from "@xplatform/design-system";
import { useTranslation } from "react-i18next";
import type { MonitoringProcessViewModel } from "@models/monitoring";
import { formatCurrency, formatDate } from "@utils/formatters";
import "./ExtractionProcessTable.scss";

interface ExtractionProcessTableProps {
    processes: MonitoringProcessViewModel[];
    onRowClick: (process: MonitoringProcessViewModel) => void;
}

export const ExtractionProcessTable = ({
    processes,
    onRowClick,
}: ExtractionProcessTableProps) => {
    const { t } = useTranslation();

    const columns: DataTableColumn<MonitoringProcessViewModel>[] = [
        {
            key: "processId",
            header: t("admin.monitoring.labels.processId"),
            width: "8%",
            render: (process) => (
                <Tooltip label={process.id} position="top">
                    <Text size="sm" className="extraction-process-table__id">
                        {process.idShort}
                    </Text>
                </Tooltip>
            ),
        },
        {
            key: "status",
            header: t("admin.monitoring.labels.status"),
            width: "12%",
            render: (process) => (
                <StatusBadge size="sm" status={process.status}>
                    {process.status}
                </StatusBadge>
            ),
        },
        {
            key: "doctype",
            header: t("admin.monitoring.processInfo.doctype"),
            width: "10%",
            render: (process) => (
                <Text size="sm" c="dimmed" truncate="end">
                    {process.doctypeName || "-"}
                </Text>
            ),
        },
        {
            key: "items",
            header: t("admin.monitoring.labels.items"),
            width: "8%",
            align: "right",
            render: (process) => (
                <Badge size="sm" variant="outline" color="gray">
                    {process.numberOfItems}
                </Badge>
            ),
        },
        {
            key: "totalCost",
            header: t("admin.monitoring.labels.totalCost"),
            width: "12%",
            align: "right",
            render: (process) => (
                <Text size="sm" fw={600}>
                    {formatCurrency(process.totalCost, process.currency)}
                </Text>
            ),
        },
        {
            key: "events",
            header: t("admin.monitoring.labels.events"),
            width: "8%",
            align: "right",
            render: (process) => <Text size="sm">{process.eventCount.toLocaleString()}</Text>,
        },
        {
            key: "createdBy",
            header: t("admin.monitoring.labels.createdBy"),
            width: "14%",
            render: (process) => (
                <Text size="sm" c="dimmed" truncate="end">
                    {process.createdBy || "-"}
                </Text>
            ),
        },
        {
            key: "organisation",
            header: t("admin.monitoring.labels.organisation"),
            width: "14%",
            render: (process) => (
                <Text size="sm" c="dimmed" truncate="end">
                    {process.organisationName || "-"}
                </Text>
            ),
        },
        {
            key: "created",
            header: t("admin.monitoring.labels.created"),
            render: (process) => (
                <Text size="sm" c="dimmed">
                    {formatDate(process.createdAt)}
                </Text>
            ),
        },
    ];

    return (
        <DataTable<MonitoringProcessViewModel>
            className="extraction-process-table"
            fz="sm"
            verticalSpacing="xs"
            horizontalSpacing="md"
            columns={columns}
            data={processes}
            getRowKey={(process) => process.id}
            onRowClick={(process) => onRowClick(process)}
        />
    );
};
